import React, { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaPhone, FaEnvelope, FaGlobe, FaCheckCircle } from 'react-icons/fa';
const WEB3FORM_ACCESS_KEY = process.env.REACT_APP_WEB3FORM_ACCESS_KEY;

const DemoModal = ({ isOpen, onClose, selectedLanguage = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    language: selectedLanguage
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectedLanguage) {
      setFormData(prev => ({ ...prev, language: selectedLanguage }));
    }
  }, [selectedLanguage, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.language) {
      newErrors.language = 'Please select a language';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      if (!WEB3FORM_ACCESS_KEY) {
        console.error('WEB3FORM_ACCESS_KEY is not configured');
        alert('Form temporarily unavailable. Please try again later.');
        return;
      }
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            access_key: WEB3FORM_ACCESS_KEY,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            language: formData.language,
            subject: `Free Demo Request - ${formData.language}`,
            from_name: 'The Lingua Alliance - Demo Request'
          })
        });

        const result = await response.json();
        
        if (result.success) {
          console.log('Demo request submitted successfully:', result);
          // Show success modal
          setShowSuccess(true);
          
          // Reset form
          setFormData({
            name: '',
            phone: '',
            email: '',
            language: ''
          });
          setErrors({});
        } else {
          console.error('Form submission failed:', result);
          alert('Failed to submit form. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('An error occurred. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      language: ''
    });
    setErrors({});
    setShowSuccess(false);
    onClose();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Success Modal */}
      {showSuccess && (
        <div className="relative z-10 w-full max-w-md mx-4 animate-[slideUp_0.3s_ease-out]">
          <div className="rounded-3xl bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,0.3)]">
            <div className="text-center">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 text-4xl text-white mb-6 animate-[scaleIn_0.5s_ease-out]">
                <FaCheckCircle />
              </div>
              <h3 className="text-2xl font-bold text-ink mb-4">
                Thank You!
              </h3>
              <p className="text-lg text-ink-muted mb-8">
                Your demo request has been received. Our team will connect with you soon to schedule your free session!
              </p>
              <button
                onClick={handleSuccessClose}
                className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)]"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {!showSuccess && (
        <div className="relative z-10 w-full max-w-lg mx-4 animate-[slideUp_0.3s_ease-out]">
          <div className="rounded-3xl bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,0.3)]">
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-6 right-6 text-ink-muted hover:text-ink transition-colors"
              aria-label="Close modal"
            >
              <FaTimes className="text-2xl" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-ink mb-2">
                Book Your Free Demo
              </h2>
              <p className="text-ink-muted">
                Take the first step towards mastering a new language
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-ink mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                    <FaUser />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className={`w-full rounded-xl border ${errors.name ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-ink mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                    <FaPhone />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className={`w-full rounded-xl border ${errors.phone ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-ink mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary">
                    <FaEnvelope />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className={`w-full rounded-xl border ${errors.email ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Language Dropdown */}
              <div>
                <label htmlFor="language" className="block text-sm font-semibold text-ink mb-2">
                  Select Language *
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary pointer-events-none">
                    <FaGlobe />
                  </div>
                  <select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    className={`w-full rounded-xl border ${errors.language ? 'border-red-500' : 'border-primary/20'} bg-white pl-12 pr-4 py-3 text-ink appearance-none cursor-pointer focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                  >
                    <option value="">Choose a language</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                    <option value="Spanish">Spanish</option>
                    <option value="Japanese">Japanese</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="h-5 w-5 text-ink-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {errors.language && (
                  <p className="mt-1 text-sm text-red-500">{errors.language}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)] mt-6"
              >
                Book My Free Demo
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoModal;
